import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
} from 'react';
import { cva, cx, VariantProps } from 'class-variance-authority';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import { RadioGroupItemProps } from './Item';
import { Label } from '../Label';

const root = cva(['flex', 'gap-4'], {
  variants: {
    direction: {
      row: ['flex-row'],
      col: ['flex-col'],
    },
  },

  defaultVariants: {
    direction: 'col',
  },
});

type RadioGroupRootTypeMap = BaseTypeMap<
  'div',
  {
    name: string;
    label: string;
    children: ReactElement<RadioGroupItemProps>[];
    onChange?: RadioGroupItemProps['onChange'];
    value?: RadioGroupItemProps['value'];
    required?: boolean;
  } & VariantProps<typeof root>
>;

export type RadioGroupRootProps = ComponentProps<RadioGroupRootTypeMap>;

export const RadioGroupRoot: Component<RadioGroupRootTypeMap> = forwardRef(
  function RadioGroupRoot(
    {
      children,
      className,
      name,
      onChange,
      direction,
      value,
      label,
      required,
      ...rest
    },
    ref
  ) {
    return (
      <Box ref={ref} className={cx(root({ direction }), className)} {...rest}>
        <Label>{!required ? `${label}` : `${label} *`}</Label>

        {Children.map(
          children,
          (child, i) =>
            isValidElement(child) &&
            cloneElement(
              child as ReactElement<{
                name?: string;
                onChange: RadioGroupItemProps['onChange'];
                checked?: boolean;
                value?: string;
                id?: string;
              }>,
              {
                name,
                onChange,
                checked: child.props.value === value,
                id: `${name}-${i}`,
              }
            )
        )}
      </Box>
    );
  }
);
